import { getRandomColor } from 'module/helpers/get-random-color';
import {
  Checkbox,
  Tag,
  type Color,
  type PopoverSelectGroup,
  type PopoverSelectProps,
} from '@rocketlane/ui';
import { SourcePageTemplate } from 'api/custom-section/types';
import { PageTemplateLite } from 'api/page-template/types';
import { keyBy } from 'lodash';
import { useMemo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { PageTemplateSourceSelector } from 'components/organisms/template-sources/source-selector';
import { useTemplatePageSources } from 'components/organisms/template-sources/use-sources';

const ListWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-02);
`;

const toPackageColor = (color: ReturnType<typeof getRandomColor>): Color =>
  color === 'white' ? 'outline' : color;

type GroupActionContext = Parameters<
  NonNullable<PopoverSelectProps['renderGroupAction']>
>[1];

interface GroupSelectActionProps extends GroupActionContext {
  group: PopoverSelectGroup;
}

const GroupSelectAction = ({
  group,
  value,
  setValue,
}: GroupSelectActionProps) => {
  const currentSelected = useMemo(
    () => (Array.isArray(value) ? value : []),
    [value],
  );
  const selectedSet = useMemo(
    () => new Set(currentSelected.map(({ value }) => value)),
    [currentSelected],
  );
  const optionSet = useMemo(
    () => new Set(group.items.map(({ value }) => value)),
    [group.items],
  );

  const isAllSelected = useMemo(
    () => group.items.every(({ value }) => selectedSet.has(value)),
    [group.items, selectedSet],
  );

  const handleCheckboxClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (isAllSelected) {
        setValue(currentSelected.filter(({ value }) => !optionSet.has(value)));
      } else {
        setValue([
          ...currentSelected,
          ...group.items.filter(({ value }) => !selectedSet.has(value)),
        ]);
      }
    },
    [
      currentSelected,
      group.items,
      isAllSelected,
      optionSet,
      selectedSet,
      setValue,
    ],
  );

  return (
    <Checkbox
      aria-label={`Select all ${group.label}`}
      onClick={handleCheckboxClick}
      checked={isAllSelected}
      color="black"
    />
  );
};

const MaximumVisibleTags = 2;

interface Props {
  value?: SourcePageTemplate[];
  onChange?: (value: SourcePageTemplate[]) => void;
}
export const PeoplePageSourceDerivedQuestionSectionSourceSelector = ({
  value,
  onChange: onChangeProp,
}: Props) => {
  const { templatesByPage, isLoading: isTemplatePageLoading } =
    useTemplatePageSources();
  const allTemplatesById = useMemo(() => {
    return keyBy(Object.values(templatesByPage).flat(), 'templateId');
  }, [templatesByPage]);
  const valueForSelect = useMemo(() => {
    return (value ?? [])
      .map(({ templateId }) => allTemplatesById[templateId])
      .filter(Boolean);
  }, [allTemplatesById, value]);
  const selected = useMemo(() => {
    return isTemplatePageLoading ? (value ?? []) : valueForSelect;
  }, [isTemplatePageLoading, value, valueForSelect]);
  const allTemplatesSelected = useMemo(() => {
    return Object.values(allTemplatesById).length === selected.length;
  }, [allTemplatesById, selected.length]);
  const displayableValues = useMemo(() => {
    if (selected.length > MaximumVisibleTags) {
      return selected.slice(0, MaximumVisibleTags);
    }

    return selected;
  }, [selected]);

  const moreCount = useMemo(() => {
    return selected.length - displayableValues.length;
  }, [selected, displayableValues]);
  const onChange = useCallback(
    (next: PageTemplateLite[]) => {
      onChangeProp?.(
        next.map((template) => ({
          templateId: template.templateId,
          templateName: template.templateName,
          pageType: template.pageType,
          sourceType: template.sourceType,
        })),
      );
    },
    [onChangeProp],
  );

  return (
    <PageTemplateSourceSelector
      templatesByPage={templatesByPage}
      isLoading={isTemplatePageLoading}
      value={valueForSelect}
      onChange={onChange}
      renderGroupAction={(group, context) => (
        <GroupSelectAction
          group={group}
          value={context.value}
          setValue={context.setValue}
        />
      )}
      triggerContent={
        selected.length !== 0 && !allTemplatesSelected ? (
          <ListWrapper>
            {displayableValues.map(({ templateName, templateId }) => (
              <Tag
                key={templateName}
                color={toPackageColor(getRandomColor(templateId))}
                variant="basic"
              >
                {templateName}
              </Tag>
            ))}
            {moreCount > 0 && (
              <Tag color="gray" variant="basic">
                <FormattedMessage
                  id="plus_more"
                  values={{ count: moreCount }}
                />
              </Tag>
            )}
          </ListWrapper>
        ) : (
          <Tag color="blue" variant="basic">
            <FormattedMessage id="rich_text_editor.sections.question_section.sources.placeholder" />
          </Tag>
        )
      }
    />
  );
};
